import { apiCreateHotspot, createProductViaApi, getAllHotspotApis, getAllHotspotCameras, getAllHotspotProducts, getPropVariantFromApi, updateProductApi } from "../../api/retailAutomationApis";
import { createHotspotCamera } from "./helper";
import { blobUrlToFile } from "./common/moduleHelper";
import { getFileNameFromUrl } from "../../common/DownloadFileController/downloadFile";
import { apiPostImageOnPresignedData } from "../../helpers/api";
import { dispatcher } from "../../helpers/projectHelper";
import { retailAutomationActions, selectActiveSetDetails, selectAllVariantsData, selectHotSpotProducts } from "../../redux/slicers/retailAutomation/retailAutomation.slicer";
import { getAppState } from "../../redux/store";
import { useDispatch, useSelector } from "react-redux";

export const useRetailHotspot = () => {
  const dispatch = useDispatch();
  const { activeSet, activeHotspot, activeLinkedPropObjectId } = useSelector(selectActiveSetDetails)
  const reduxVariantData = useSelector(selectAllVariantsData);
  const allUsedHotspotsProdctsdata = useSelector(selectHotSpotProducts);

  const createHotspots = async ({
    hotspots, 
    automationId, 
    retailSetId
  }) => {
    const clonedHotspots = JSON.parse(JSON.stringify(hotspots));

    // Build payloads first
    const hotspotPromisesArr = clonedHotspots?.map((hotspot, i) => {
      const payload = {
        name: `In-Store Location ${i + 1}`,
        retail_automation: automationId,
        retail_set: retailSetId,
        json_data: JSON.stringify({
          sequence: i + 1,
          linked_prop_object_id: hotspot.linked_prop_object_id,
          linked_prop_object_name: hotspot.linked_prop_object_name,
        }),
      };

      return apiCreateHotspot(payload).then((response) => response?.data);
    });

    // Run all in parallel
    const updatedHotspots = await Promise.all(hotspotPromisesArr);

    // Filter only valid ones
    const allHotspotData = updatedHotspots.filter((data) => data?.id);
    return allHotspotData.length === clonedHotspots.length ? allHotspotData : [];
  };

  const createHotspotCameras = async ({
    hotspot,
    automationId,
    retailSetId
  }) => {
    const hotspotCameraInfo = hotspot?.json_data?.cameraInfo;
    if (!hotspotCameraInfo.length) return [];

    const newCameras = await createHotspotCamera({
      automationId,
      retailSetId: retailSetId,
      hotspotId: hotspot?.id,
      cameraList: hotspotCameraInfo,
      autoEnableFirst: true,
    })

    // Parallel upload of thumbnails
    await Promise.all(
      newCameras
        .filter((c) => c?.camera_data?.thumbnail_link && c?.presigned_data_thumb)
        .map(async (camera) => {
          const thumbnailBlob = await blobUrlToFile(
            camera.camera_data.thumbnail_link,
            getFileNameFromUrl(camera.camera_data.thumbnail_link)
          );
          return apiPostImageOnPresignedData(thumbnailBlob, camera.presigned_data_thumb);
        })
    );

    const cameras = newCameras.filter(Boolean);

    if (hotspot?.active) {
      dispatcher(retailAutomationActions.setArtworkCameraRedux(cameras));
    }

    return cameras.length === hotspotCameraInfo.length ? cameras : [];
  }

  const createHotspotProduct = async ({ hotspot }) => {
    const propId = hotspot?.json_data?.linked_prop_object_id;
    if (!propId) return [];

    const reduxVariantData = selectAllVariantsData(getAppState());
    let allProducts = [];

    if (reduxVariantData?.[propId]?.length) {
      allProducts = JSON.parse(JSON.stringify(reduxVariantData[propId]));
    } else {
      allProducts = await getPropVariantFromApi(propId);
      allProducts = JSON.parse(JSON.stringify(allProducts));
      const clonedReduxVariantData = JSON.parse(JSON.stringify(reduxVariantData));
      clonedReduxVariantData[propId] = allProducts;
      dispatcher(retailAutomationActions.setAllVariantsData(clonedReduxVariantData));
    }

    // Run all API calls in parallel
    const newProducts = await Promise.all(
      allProducts.map(async (prod) => {
        const payload = {
          name: prod?.name,
          retail_hotspot: hotspot?.id,
          product: prod.id,
          thumb: prod?.thumbnail,
        };
        const response = await createProductViaApi(payload);
        if (response?.data?.product) {
          response.data.product = parseInt(response.data.product);
        }
        return response?.data;
      })
    );

    // Update Redux
    let savedHotspotProducts = selectHotSpotProducts(getAppState()) || {};
    savedHotspotProducts = JSON.parse(JSON.stringify(savedHotspotProducts));

    dispatcher(retailAutomationActions.setHotSpotProducts([ { id: hotspot?.id, products: newProducts.filter(Boolean) } ]));

    return newProducts.length === allProducts.length ? newProducts : [];
  }

  const getHotspotCamera = async ({
    hotspot,
    retail_set
  }) => {
    const allHotspotCameras = await getAllHotspotCameras({ hotspot, retail_set });
    return allHotspotCameras;
  };

  const getHotspotList = async ({
    retailAutomationId,
    retailSetId
  }) => {
    const allHotspot = await getAllHotspotApis({ retailAutomationId, retailSetId });
    return allHotspot;
  }

  // const customizeAndAddProduct = async ({ fetchFreshData= false, setHotspotProducts }) => {
  //   if (activeLinkedPropObjectId) {
  //     let variants = [];
  //     if (reduxVariantData?.[activeLinkedPropObjectId]?.length) {
  //       variants = JSON.parse(JSON.stringify(reduxVariantData?.[activeLinkedPropObjectId]));
  //     } else {
  //       variants = await getPropVariantFromApi(activeLinkedPropObjectId);
  //       variants = JSON.parse(JSON.stringify(variants));
  //       const copyData = JSON.parse(JSON.stringify(reduxVariantData));
  //       copyData[activeLinkedPropObjectId] = variants;
  //       dispatch(retailAutomationActions.setAllVariantsData(copyData));
  //     }

  //     let _products = variants.reduce((acc, curr) => {
  //       const newObj = JSON.parse(JSON.stringify(curr));
  //       newObj['mainProduct'] = true;
  //       newObj['selected'] = true;
  //       acc.push(newObj);

  //       if (newObj.product_variants_data) {
  //         acc.push(...newObj.product_variants_data);
  //       }

  //       return acc;
  //     }, []);

  //     setHotspotProducts([..._products]);
  //     let allProducts;
  //     if (allUsedHotspotsProdctsdata[activeHotspot?.id]?.length && !fetchFreshData) {
  //       allProducts = JSON.parse(JSON.stringify(allUsedHotspotsProdctsdata[activeHotspot?.id]));
  //     } else {
  //       allProducts = await getAllHotspotProducts({
  //         hotspot: activeHotspot?.id,
  //         retail_set: activeSet.id,
  //       });
  //     }
  //     allProducts = await Promise.all(
  //       allProducts?.map((prodData) => {
  //         const foundProd = _products?.find((val) => val?.id === prodData?.product);
  //         prodData['thumbnail'] = foundProd?.thumbnail;
  //         return prodData;
  //       })
  //     );
  //     allProducts = allProducts.sort((a, b) => a.id - b.id);

  //     const _productsData = [{ id: activeHotspot?.id, products: allProducts }];

  //     dispatch(retailAutomationActions.setHotSpotProducts(_productsData));
  //     dispatch(retailAutomationActions.setCurrentHotSpotProductsList(allProducts));
  //   }
  // }

  const customizeAndAddProduct = async ({ setHotspotProducts, fetchFreshData= false }) => {
    if (!activeLinkedPropObjectId) return;

    // Fetch or get variants from Redux
    let variants = reduxVariantData?.[activeLinkedPropObjectId];
    if (!variants?.length) {
      variants = await getPropVariantFromApi(activeLinkedPropObjectId);
      dispatch(
        retailAutomationActions.setAllVariantsData({
          ...reduxVariantData,
          [activeLinkedPropObjectId]: variants,
        })
      );
    }

    // Build hotspot products
    const _products = variants.flatMap((variant) => {
      const mainProduct = { ...variant, mainProduct: true, selected: true };
      return variant.product_variants_data
        ? [mainProduct, ...variant.product_variants_data]
        : [mainProduct];
    });

    // Update local state
    setHotspotProducts?.([..._products]);

    // Fetch all products for the hotspot
    let allProducts = allUsedHotspotsProdctsdata[activeHotspot?.id] || [];
    if (!allProducts.length || fetchFreshData) {
      allProducts = await getAllHotspotProducts({
        hotspot: activeHotspot?.id,
        retail_set: activeSet.id,
      });
    }

    // Attach thumbnails and sort
    allProducts = allProducts
      .map((prodData) => {
        const foundProd = _products.find((p) => p.id === prodData.product);
        return { ...prodData, thumbnail: foundProd?.thumbnail };
      })
      .sort((a, b) => a.id - b.id);

    // Dispatch to Redux
    const _productsData = [{ id: activeHotspot?.id, products: allProducts }];
    dispatch(retailAutomationActions.setHotSpotProducts(_productsData));
    dispatch(retailAutomationActions.setCurrentHotSpotProductsList(allProducts));
  }

  const deleteHotspot = () => {};

  return { createHotspots, customizeAndAddProduct, createHotspotCameras, createHotspotProduct, getHotspotCamera, getHotspotList, deleteHotspot };
};
